/* LangMenu — language dropdown for the global topbar + ForecastMarket head.
 *
 * Replaces the previous segmented (IT|EN) control. Marco's PR 2 feedback:
 * "The language buttons should be a dropdown menu." A menu is more
 * scalable (when we add Spanish / German later, a segmented would not
 * fit the topbar) and reads as more refined than a segmented for a
 * single-pick utility setting.
 *
 * Visual: small ghost button showing the current language code (EN / IT)
 * with a caret. Click opens a 2-item popover. Click outside / Escape /
 * pick → closes.
 *
 * Behavior: writes via setLang() from lib/lang.ts so all other
 * surfaces (ForecastMarket.state.lang, useLang() consumers) stay in
 * sync via the broadcast event.
 *
 * Styling: uses --ds-* tokens so radius / hover follow the active design.
 */
import React from "react";
import { useLang, setLang, type Lang } from "../../lib/lang";

const OPTIONS: { id: Lang; label: string; full: { en: string; it: string } }[] = [
  { id: "en", label: "EN", full: { en: "English", it: "Inglese"  } },
  { id: "it", label: "IT", full: { en: "Italian", it: "Italiano" } },
];

export default function LangMenu() {
  const lang = useLang();
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const aria = lang === "it" ? "Lingua" : "Language";

  return (
    <div ref={wrapRef} className="lang-menu-wrap">
      <button
        type="button"
        className={`lang-menu__trigger ${open ? "lang-menu__trigger--open" : ""}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={aria}
        title={aria}
      >
        <span className="lang-menu__current">{lang.toUpperCase()}</span>
        <svg className="lang-menu__caret" width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path d="M2 4 L5 7 L8 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="lang-menu__panel" role="menu">
          {OPTIONS.map(opt => (
            <button
              key={opt.id}
              type="button"
              role="menuitem"
              className={`lang-menu__item ${lang === opt.id ? "lang-menu__item--active" : ""}`}
              onClick={() => { setLang(opt.id); setOpen(false); }}
            >
              <span className="lang-menu__item-code">{opt.label}</span>
              <span className="lang-menu__item-name">{lang === "it" ? opt.full.it : opt.full.en}</span>
              {lang === opt.id && (
                <svg className="lang-menu__check" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M2 6 L5 9 L10 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
